/**
 * Server-side parser that splits blog-post HTML into renderable parts:
 *  - 'html'     : a plain HTML chunk (rendered via dangerouslySetInnerHTML)
 *  - 'widget'   : an inline tool widget marker (existing pattern)
 *  - 'affiliate': the injected "Recommended Products" block, parsed into
 *                 per-product records + the original HTML as a fallback
 *
 * The parser runs server-side so the page can pre-render enhanced affiliate
 * card components without a hydration flash.
 */

import type { PickBadgeTone } from '@/data/article-product-picks'

export interface ParsedAffiliateProduct {
  /** ASIN extracted from the anchor href; null when the href is a shortlink (amzn.to) or otherwise unparseable */
  asin: string | null
  /** Text inside the anchor tag (product name as written in the article) */
  name: string
  /** Original href from the Directus content — used as the CTA target; do not reconstruct */
  href: string
  /** Editorial description that follows the anchor (already matched to the article tone) */
  description: string
  /** Optional badge parsed from data-badge="Label|tone" attribute on the <li> */
  badge?: { label: string; tone?: PickBadgeTone }
}

export type ContentPart =
  | { type: 'html'; html: string }
  | { type: 'widget'; tool: string; variant: string; slug?: string; payload?: string }
  | { type: 'affiliate'; products: ParsedAffiliateProduct[]; fallbackHtml: string }

// Matches the inline widget markers that are already used in blog content, e.g.
//   <div data-tool="product-pick" data-variant="0" data-slug="my-slug"></div>
// Intentionally self-closing; multiline content is not supported.
const WIDGET_MARKER_REGEX = /<div\s[^>]*?data-tool="([^"]+)"[^>]*?data-variant="([^"]+)"[^>]*?><\/div>/g
const SLUG_ATTR_REGEX = /data-slug="([^"]+)"/
// Opaque per-widget data (JSON for the species strip). Entity-decoded like the
// other attributes; the consumer parses it.
const PAYLOAD_ATTR_REGEX = /data-payload="([^"]*)"/

// Attribute values are captured from raw (sanitized) HTML, so entities arrive
// encoded — e.g. data-variant="Fertilizers &amp; Soil Health". Consumers compare
// these against plain strings (Directus category `_eq` filters), so decode the
// handful of entities the sanitizer emits.
function decodeAttrEntities(value: string): string {
  return value
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
}

// Matches the affiliate block injected by lib/article-affiliate-recommendations.ts:403-410:
//   <div data-affiliate-block="true"><p>...</p><ul>...</ul></div>
// The [\s\S]*? is non-greedy so nested blocks (if any ever appear) won't be swallowed.
// IMPORTANT: this regex targets the known generated format only. If Directus ever
// nests additional <div>s inside, switch to a real HTML parser (cheerio is already a dep).
const AFFILIATE_BLOCK_REGEX = /<div\s[^>]*?data-affiliate-block="true"[^>]*?>([\s\S]*?)<\/div>/g

// Parses one <li>...</li> row inside the affiliate block.
// Format written by lib/article-affiliate-recommendations.ts:407:
//   <li><strong><a href="URL" ...>Name</a></strong> - description</li>
// Optionally: <li data-badge="Label|tone">...</li>
const LI_REGEX = /<li([^>]*)>([\s\S]*?)<\/li>/g
const ANCHOR_REGEX = /<a\s[^>]*?href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/i
const BADGE_ATTR_REGEX = /data-badge="([^"]+)"/i

const VALID_BADGE_TONES: ReadonlySet<PickBadgeTone> = new Set([
  'gold', 'green', 'blue', 'purple', 'orange', 'slate',
])

function parseBadgeAttr(attrs: string): { label: string; tone?: PickBadgeTone } | undefined {
  const m = attrs.match(BADGE_ATTR_REGEX)
  if (!m) return undefined
  const raw = decodeEntities(m[1])
  const [labelRaw, toneRaw] = raw.split('|')
  const label = labelRaw?.trim()
  if (!label) return undefined
  const tone = toneRaw?.trim() as PickBadgeTone | undefined
  return tone && VALID_BADGE_TONES.has(tone) ? { label, tone } : { label }
}

import { resolveShortlinkAsin } from '@/lib/affiliate-shortlink-aliases'

const ASIN_REGEX = /\/dp\/([A-Z0-9]{10})/

function stripTags(html: string): string {
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim()
}

function decodeEntities(s: string): string {
  return s
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
}

export function parseAffiliateBlock(innerHtml: string): ParsedAffiliateProduct[] {
  const out: ParsedAffiliateProduct[] = []
  const liRegex = new RegExp(LI_REGEX.source, LI_REGEX.flags)
  let m: RegExpExecArray | null
  while ((m = liRegex.exec(innerHtml)) !== null) {
    const liAttrs = m[1] ?? ''
    const liBody = m[2]
    const anchorMatch = liBody.match(ANCHOR_REGEX)
    if (!anchorMatch) continue

    const href = decodeEntities(anchorMatch[1])
    const name = decodeEntities(stripTags(anchorMatch[2]))

    // Everything after the closing </a> is the description; trim leading separator chars.
    const afterAnchor = liBody.slice((anchorMatch.index ?? 0) + anchorMatch[0].length)
    const description = decodeEntities(stripTags(afterAnchor)).replace(/^[\s\-\u2013\u2014:]+/, '').trim()

    const asinMatch = href.match(ASIN_REGEX)
    const asin = asinMatch ? asinMatch[1] : resolveShortlinkAsin(href)

    // Badge attribute can live on the <li> or inside the anchor; check both.
    const badge = parseBadgeAttr(liAttrs) ?? parseBadgeAttr(anchorMatch[0])

    out.push({ asin, name, href, description, ...(badge ? { badge } : {}) })
  }
  return out
}

/**
 * Locates every affiliate block in a document, in order, with the offsets a
 * caller needs to splice one out or move it.
 *
 * Shares AFFILIATE_BLOCK_REGEX with parseBlogContent so remediation scripts see
 * exactly the blocks the renderer sees. That regex is permissive about
 * attribute order — `<div class="x" data-affiliate-block="true">` matches — which
 * ad-hoc `<div data-affiliate-block="true">` string searches miss.
 *
 * The module-level regex is global (and therefore stateful), so a fresh instance
 * is built per call; the shared source is never exec'd directly.
 */
export function findAffiliateBlocks(
  html: string
): Array<{ index: number; length: number; html: string }> {
  if (!html) return []
  const out: Array<{ index: number; length: number; html: string }> = []
  const re = new RegExp(AFFILIATE_BLOCK_REGEX.source, AFFILIATE_BLOCK_REGEX.flags)
  let m: RegExpExecArray | null
  while ((m = re.exec(html)) !== null) {
    out.push({ index: m.index, length: m[0].length, html: m[0] })
  }
  return out
}

/**
 * Splits HTML into parts. Consumes widget markers AND affiliate blocks in one pass.
 * Returns parts in document order so the caller can render them back-to-back.
 */
export function parseBlogContent(html: string): ContentPart[] {
  if (!html) return []

  type Match = { index: number; length: number; part: ContentPart }
  const matches: Match[] = []

  const widgetRe = new RegExp(WIDGET_MARKER_REGEX.source, WIDGET_MARKER_REGEX.flags)
  let wm: RegExpExecArray | null
  while ((wm = widgetRe.exec(html)) !== null) {
    const slugMatch = wm[0].match(SLUG_ATTR_REGEX)
    const payloadMatch = wm[0].match(PAYLOAD_ATTR_REGEX)
    matches.push({
      index: wm.index,
      length: wm[0].length,
      part: {
        type: 'widget',
        tool: decodeAttrEntities(wm[1]),
        variant: decodeAttrEntities(wm[2]),
        slug: slugMatch?.[1],
        ...(payloadMatch ? { payload: decodeAttrEntities(payloadMatch[1]) } : {}),
      },
    })
  }

  const blockRe = new RegExp(AFFILIATE_BLOCK_REGEX.source, AFFILIATE_BLOCK_REGEX.flags)
  let bm: RegExpExecArray | null
  while ((bm = blockRe.exec(html)) !== null) {
    const fallbackHtml = bm[0]
    const products = parseAffiliateBlock(bm[1])
    matches.push({
      index: bm.index,
      length: bm[0].length,
      part: { type: 'affiliate', products, fallbackHtml },
    })
  }

  matches.sort((a, b) => a.index - b.index)

  const parts: ContentPart[] = []
  let cursor = 0
  for (const m of matches) {
    if (m.index > cursor) {
      parts.push({ type: 'html', html: html.slice(cursor, m.index) })
    }
    parts.push(m.part)
    cursor = m.index + m.length
  }
  if (cursor < html.length) {
    parts.push({ type: 'html', html: html.slice(cursor) })
  }

  return parts
}
